<script>
import ActionButtonGroup from './components/ActionButtonGroup.vue'
import CertificationCallout from './components/CertificationCallout.vue'
import LegalDisclaimer from './components/LegalDisclaimer'
import MarketValue from './components/MarketValue/index.vue'
import MarketValueTable from './components/MarketValue/MarketValueTable.vue'
import PhotoGallery from './components/PhotoGallery.vue'
import PremiumEquipment from './components/PremiumEquipment.vue'
import ServiceUpgrades from './components/ServiceUpgrades/index.vue'
import Vehicle404 from './components/Vehicle404.vue'
import VehicleAttributes from './components/VehicleAttributes.vue'
import VehicleDescription from './components/VehicleDescription'
import VehicleEquipment from './components/VehicleEquipment.vue'
import VehicleHighlights from './components/VehicleHighlights.vue'
import VehicleHistory from './components/VehicleHistory/index.vue'
import VehicleSummary from './components/VehicleSummary.vue'
import { dataSource, loadVehicle } from './data-source'
import pluggable from '~/mixins/pluggable.js'
import DealerInfo from '~/patterns/DealerInfo'

export default {
  name: 'VDP',
  components: {
    ActionButtonGroup,
    CertificationCallout,
    DealerInfo,
    LegalDisclaimer,
    MarketValue,
    MarketValueTable,
    PhotoGallery,
    PremiumEquipment,
    ServiceUpgrades,
    VehicleAttributes,
    VehicleDescription,
    VehicleEquipment,
    VehicleHighlights,
    VehicleHistory,
    VehicleSummary,
    Vehicle404
  },
  mixins: [
    pluggable('vdp')
  ],
  provide: { dataSource },
  props: {
    // Optional prop to receive dealer phone number from the path-store instance
    dealerPhone: {
      type: String,
      default: null
    },
    disableClientLoad: {
      type: Boolean,
      default: false
    },
    inventory: {
      type: [Number, String],
      required: true
    }
  },
  computed: {
    dealerId: () => dataSource.dealer.id,
    inventoryId: () => dataSource.inventory.id,
    hasLoadedMarketListing: () => Object.keys(dataSource.marketListings).length > 0,
    highlights: () => dataSource.inventory.highlights,
    loading: vm =>
      dataSource.actions.getVehicle
        .isPending(({ inventoryId }) => inventoryId === vm.inventory),
    showMarketListingsPrintVersion: vm =>
      dataSource.isMarketValuePanelOpen &&
      vm.hasLoadedMarketListing,
    vin: () => dataSource.inventory.vehicle?.vin,
    ymmt: () => dataSource.vehicle?.ymmt
  },
  watch: {
    inventory: {
      handler (newValue) {
        loadVehicle(newValue)
      },
      immediate: true
    }
  },
  async destroyed () {
    // clear vehicle data when navigating away from VDP
    // prevents old vehicle data from showing when loading VDP again
    loadVehicle(false)
  }
}
</script>

<template>
  <VContainer
    v-if="!loading"
    class="container--vdp"
    fluid
  >
    <VLayout
      v-if="vin"
      justify-space-between
      wrap
    >
      <VFlex
        v-if="$vuetify.breakpoint.mdAndUp"
        class="md6 vdp--left-container"
      >
        <PhotoGallery class="mb-6" />
        <VehicleAttributes class="my-6" />
        <VehicleHighlights
          :highlights="highlights"
        />
        <VehicleEquipment class="my-6" />
        <VehicleHistory class="my-6" />
        <ServiceUpgrades class="my-6" />
        <VehicleDescription class="my-6" />
      </VFlex>
      <VFlex
        v-if="$vuetify.breakpoint.mdAndUp"
        class="md6 vdp--right-container"
      >
        <VehicleSummary />
        <ActionButtonGroup
          :inventory="inventory"
        />
        <MarketValue />
        <PremiumEquipment class="my-6" />
        <CertificationCallout class="my-6" />
        <DealerInfo
          class="my-6"
          :dealer-phone="dealerPhone"
          :dealer-id="dealerId"
        />
      </VFlex>
      <MarketValueTable
        v-if="showMarketListingsPrintVersion"
        class="d-none d-print-block"
        :ymmt="ymmt"
        :inventory-id="inventoryId"
        expanded
      />
      <VFlex
        v-if="$vuetify.breakpoint.smAndDown"
        class="xs12 sm12"
      >
        <PhotoGallery class="mb-6" />
        <VehicleSummary />
        <ActionButtonGroup
          :inventory="inventory"
        />
        <MarketValue />
        <VehicleAttributes class="my-6" />
        <PremiumEquipment class="my-6" />
        <VehicleHighlights
          :highlights="highlights"
        />
        <VehicleEquipment class="my-6" />
        <VehicleHistory class="my-6" />
        <ServiceUpgrades class="my-6" />
        <CertificationCallout class="my-6" />
        <DealerInfo
          class="my-6"
          :dealer-phone="dealerPhone"
          :dealer-id="dealerId"
        />
        <VehicleDescription class="my-6" />
      </VFlex>
    </VLayout>
    <slot
      v-else
      name="404"
    >
      <Vehicle404 />
    </slot>
    <LegalDisclaimer />
  </VContainer>
</template>

<style lang="scss">
.container--vdp {
  --vdp-container-padding: 12px;

  // Override the default container padding to ensure proper sync with gallery custom margin
  padding: var(--vdp-container-padding);

  .vdp--left-container {
    padding-right: 16px;
  }

  .vdp--right-container {
    padding-left: 16px;
  }

  @include breakpoint('xs-only') {
    & .gallery__thumbs {
      margin-top: -16px;
      // Sync negative margin with container padding to prevent horizontal scroll
      margin-left: calc(-1 * var(--vdp-container-padding));
      margin-right: calc(-1 * var(--vdp-container-padding));
    }
  }

  .v-btn {
    text-transform: none;
  }
}

@media print {
  .container--vdp {
    padding: 12px 0;
  }

  .dealer-action-button {
    display: none;
  }
}

@media print and (orientation: portrait) {
  .vdp__market-value__comparison {
    grid-template-columns: unset;
  }

  .vdp--left-container {
    padding-right: 8px !important;
  }

  .vdp--right-container {
    padding-left: 8px !important;
  }

  .vdp--left-container,
  .vdp--right-container {
    width: 50%;
  }
}
</style>
